'use client';

import { useState, useRef, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { Globe, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Locale } from '@/lib/i18n';
import { localeNames } from '@/lib/i18n';

const localeFlags: Record<Locale, string> = {
    en: '🇬🇧',
    pt: '🇧🇷',
    es: '🇪🇸',
    hi: '🇮🇳',
};

function getLocaleFromPath(pathname: string): Locale {
    const seg = pathname.split('/')[1];
    if (seg === 'pt' || seg === 'es' || seg === 'hi') return seg;
    return 'en';
}

function getLocalizedPath(targetLocale: Locale): string {
    if (targetLocale === 'en') return '/';
    return `/${targetLocale}`;
}

export default function LanguageSelector() {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const pathname = usePathname();
    const currentLocale = getLocaleFromPath(pathname);

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={ref} className="relative flex items-center shadow-sm rounded-full">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold text-[var(--color-md-on-surface-variant)] hover:text-[var(--color-md-on-surface)] hover:bg-[var(--color-md-surface-variant)] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-md-primary)]"
                aria-label="Select language"
            >
                <Globe className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{localeNames[currentLocale]}</span>
                <span className="sm:hidden">{localeFlags[currentLocale]}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                        transition={{ duration: 0.15, ease: [0.2, 0, 0, 1] }}
                        className="absolute right-0 top-full mt-2 w-44 bg-[var(--color-md-surface)] border border-[var(--color-md-outline)] rounded-2xl shadow-cinematic-2 overflow-hidden z-50"
                    >
                        {(Object.keys(localeNames) as Locale[]).map((loc) => (
                            <Link
                                key={loc}
                                href={getLocalizedPath(loc)}
                                onClick={() => setOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${loc === currentLocale
                                        ? 'bg-[var(--color-md-primary)]/10 text-[var(--color-md-primary)] font-bold'
                                        : 'text-[var(--color-md-on-surface)] hover:bg-[var(--color-md-surface-variant)]'
                                    }`}
                            >
                                <span className="text-base">{localeFlags[loc]}</span>
                                <span>{localeNames[loc]}</span>
                                {loc === currentLocale && (
                                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[var(--color-md-primary)]" />
                                )}
                            </Link>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
